<template>
  <div :data-date="item.startDate" class="relative">
    <p v-if="showMonth" class="absolute -top-22 font-bold">{{ item.monthName }}</p>

    <div
      class="border-b-3 border-app-color pb-10 mb-20"
      :class="{'border-link-color text-link-color': isToday}"
    >
      <p class="text-15" :class="isToday ? '': 'text-grey-675'">{{ item.dayName }}</p>
      <div class="flex items-center justify-between">
        <p class="text-24 font-extrabold leading-small">{{ item.date }}</p>
        <p v-if="isToday" class="text-14 font-bold">today</p>
      </div>
    </div>

    <draggable
      v-model="computedEvents"
      group="events"
      :delay="300"
      delay-on-touch-only="true"
      ghost-class="my-sortable-ghost"
      chosen-class="grab-class"
      drag-class="grab-class"
      item-key="id"
      handle=".drag-handler"
      filter=".no-drag"
      :force-fallback="true"
      :fallback-class="isDarkMode ? 'force-fallback-dark' : 'force-fallback-light'"
      class="min-h-100"
      :class="{'absolute w-full h-full': computedEvents.length === 0}"
      @add="add"
      @start="onDragSrart"
      @end="onDragEnd"
    >
      <template #item="{ element, index }">
        <Event
          :ref="el => eventRefs[index] = el"
          :event="element"
          :selected-books="selectedBooks"
          :last-week-date="lastWeekDate"
          :first-week-date="firstWeekDate"
        />
      </template>
    </draggable>

    <div>
      <AddEvent
        v-if="showNewEvent"
        :selected-books="selectedBooks"
        :start-date="item.startDate"
        :last-week-date="lastWeekDate"
        :first-week-date="firstWeekDate"
        @hideNewEvent="hideNewEvent"
      />
    </div>

    <ButtonModule
      text="add event"
      class="text-15 font-normal text-grey-250 hover:text-dark-600 relative z-10 py-8 px-10 rounded-6 anim-color-200"
      prefix="icon-add text-10 mt-2"
      :class="isDarkMode ? 'hover:bg-dark-500' : 'hover:bg-menu-hover'"
      @click.stop="addEvent"
    />
  </div>
</template>

<script>
import Event from '@/components/week-planner/Event'
import AddEvent from '@/components/week-planner/AddEvent'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import draggable from 'vuedraggable'
import { computed, ref, onBeforeUpdate } from 'vue'
import { useStore } from 'vuex'
import { plannerService } from '@/services'

export default {
  components: { Event, AddEvent, ButtonModule, draggable },

  props: {
    item: Object,
    dayIndex: Number,
    selectedBooks: Array,
    lastWeekDate: String,
    firstWeekDate: String
  },

  setup (props) {
    const { state, getters, commit, dispatch } = useStore()

    const eventRefs = ref([])

    const showNewEvent = ref(false)

    const showMonth = computed(() => props.dayIndex !== 0 && props.item.day === '01')
    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const events = computed(() => state.planner.events)

    const computedEvents = computed({
      get: () => {
        return events.value
          .filter(el => el.showAt === props.item.startDate)
          .sort((a, b) => {
            if (a.order > b.order) return 1
            if (a.order < b.order) return -1
            return 0
          })
      },
      set: (val) => {
        const ids = {}

        val.forEach((item, index) => {
          if (!ids[item.bookId]) {
            ids[item.bookId] = [item.id]
          } else {
            ids[item.bookId].push(item.id)
          }

          commit('updateEvent', { id: item.id, order: index })
        })

        setTimeout(() => {
          for (const [key, value] of Object.entries(ids)) {
            plannerService.order({ ids: value, bookId: key })
          }
        }, 100)
      }
    })

    const isToday = computed(() => {
      const currentDate = new Date().getDate()
      const currentMonth = new Date().getMonth() + 1

      return +props.item.day === currentDate && +props.item.month === currentMonth
    })

    // to reset the refs before each update
    onBeforeUpdate(() => {
      eventRefs.value = []
    })

    function hideNewEvent () {
      showNewEvent.value = false
    }

    function addEvent () {
      showNewEvent.value = true
    }

    function add (evt) {
      const id = parseInt(evt.item.id)

      const event = events.value.find(item => item.id === id)
      const startDate = evt.to.parentNode.getAttribute('data-date')
      const newIndex = evt.newIndex

      dispatch('updateEvent', {
        id: id,
        order: newIndex,
        startDate: startDate,
        showAt: startDate,
        bookId: event.bookId
      })

      commit('updateEvent', {
        id: id,
        order: newIndex,
        startDate: startDate,
        showAt: startDate
      })
    }

    function onDragSrart () {
      document.body.classList.add('grabbing')
    }

    function onDragEnd () {
      document.body.classList.remove('grabbing')
    }

    return {
      eventRefs,
      showNewEvent,
      // computeds
      isMobile,
      isDarkMode,
      isToday,
      computedEvents,
      showMonth,
      // functions
      addEvent,
      hideNewEvent,
      add,
      onDragSrart,
      onDragEnd
    }
  }
}
</script>

<style lang="scss">
.my-sortable-ghost {
  max-height: 4px;
  height: 4px;
  min-height: 4px;
  padding: 0;
  box-shadow: none;
  background: var(--link-color);
}
.my-sortable-ghost div {
  display: none;
}

.grab-class {
  opacity: .5;
  cursor: grab !important;
  border: none;
  box-shadow: none;

  &:active {
    cursor: grabbing !important;
  }
}

.force-fallback-light {
  @apply shadow-event;
  min-height: 120px;
}

.force-fallback-dark {
  @apply border-2 border-grey-500;
  min-height: 120px;
}
</style>
